import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store';
import { Roles, SortClients } from '@nrwl/rx-models';

import { HelpersSelector } from '@nrwl/rx-state-helpers';
import { UserState } from './user.state';

export const selectUserStateState: MemoizedSelector<object, UserState> =
  createFeatureSelector<UserState>('user');

export const getEvents = createSelector(selectUserStateState, (state) => {
  if (state && state.notifications) {
    return state.notifications.eventNotifications;
  }
  return null;
});

export const loading = createSelector(selectUserStateState, (state) => {
  return state.notifications.loading;
});

export const getEventsNext = createSelector(selectUserStateState, (state) => {
  return state.notifications.eventsNext;
});

export const getNewEventsIds = createSelector(selectUserStateState, (state) => {
  return state.notifications.newEvents;
});

export const getNewMessagesIds = createSelector(selectUserStateState, (state) => {
  return state.notifications.newMessages;
});

export const getMessagesNext = createSelector(selectUserStateState, (state) => {
  return state.notifications.messagesNext;
});

export const getMessages = createSelector(selectUserStateState, (state) => {
  if (state && state.notifications) {
    return state.notifications.messageNotifications;
  }
  return null;
});

export const getNotificationsAmount = createSelector(selectUserStateState, (state) => {
  if (state && state.notifications.notificationAmount) {
    return state.notifications.notificationAmount;
  }
  return null;
});

export const getUser = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value;
  }
  return null;
});

export const getUserPhoto = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.photo;
  }
  return null;
});
export const getUserFirstName = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.first_name;
  }
  return null;
});

export const getUserLastName = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.last_name;
  }
  return null;
});

export const getUserEmail = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.email;
  }
  return null;
});

export const getUserPhone = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.phone_number;
  }
  return null;
});

export const getUserName = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.username;
  }
  return null;
});

export const getUserDateFormat = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.is_eu_datetime_format;
  }
  return null;
});

export const getLocations = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const locations = state.locations;

    return locations.map((location) => {
      return HelpersSelector.transformSelect(location.name, location.id);
    });
  }
  return null;
});

export const getAccessibleLocations = createSelector(selectUserStateState, (user) => {
  if (user && user.value) {
    const locations = [...user.locations];

    if (user.value.role === Roles.SuperAdmin || user.value.role === Roles.Admin) {
      const index = locations.findIndex((loc) => loc.id === 'unassigned');
      if (index === -1) {
        locations.unshift({ name: 'Unassigned', id: 'unassigned' });
      }
    }
    locations.unshift({ name: 'All Locations', id: 'all' });

    return locations;
  }
  return null;
});

export const getPasswordReset = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.password_reset_notification;
  }
  return null;
});

export const getNewDevice = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.new_device_login_notification;
  }
  return null;
});

export const getDefaultSort = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    return state.value.default_patientlist_ordering
      ? SortClients.LastVisit
      : SortClients.Name + ',' + SortClients.FirstName;
  }
  return null;
});

export const getDefaultLocationSort = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    if (
      state.value.clinic &&
      state.value.clinic.has_location_restrictions &&
      state.value.default_clinic_location
    ) {
      const defaultLocation = state.value.default_clinic_location;

      if (state.value.role === Roles.SuperAdmin || state.value.role === Roles.Admin) {
        if (state.value.default_clinic_location === 'null') {
          const locations = ['unassigned'];
          return { hasLocations: true, locations };
        }
        if (defaultLocation !== 'unassigned') {
          const locations = ['unassigned'];
          if (defaultLocation) {
            locations.push(defaultLocation);
          }
          return { hasLocations: true, locations };
        }
      }
      return { hasLocations: true, locations: defaultLocation };
    }
    return { hasLocations: false, locations: null };
  }
  return null;
});

export const hasLeadsAccess = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'WP_PLUGIN_GALLERY_ACCESS';
    });

    return WPPluginAddOn && !WPPluginAddOn.is_expired && state.value.clinic.api_token;
  }

  return null;
});

export const isClinicianLastNameVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'VISIT_CLINICIAN_LAST_NAME';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isVisitTypeVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'VISIT_TYPE';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isProcedureTypeVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'PROCEDURE_TYPE';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isVisitAreaVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'VISIT_AREA';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isClientBMIVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'CLIENT_BMI';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isSkinToneVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'CLIENT_SKIN_TONE';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isImplantVolumeVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'VISIT_IMPLANT_VOLUME';
    });

    return WPPluginAddOn;
  }

  return null;
});

export const isImplantPlaneVisible = createSelector(selectUserStateState, (state) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === 'VISIT_IMPLANT_PLANE';
    });

    return WPPluginAddOn;
  }

  return null;
});
const checkIsAddOnActive = (state: any, addonKey: string) => {
  if (state && state.value) {
    const WPPluginAddOn = state.value.clinic.addons.find((addon) => {
      return addon.addon === addonKey;
    });

    return WPPluginAddOn;
  }
  return null;
};
export const isZenotiIdVisible = createSelector(selectUserStateState, (state) =>
  checkIsAddOnActive(state, 'CLIENT_ZENOTI_ID'),
);
export const isProviderActive = createSelector(selectUserStateState, (state) =>
  checkIsAddOnActive(state, 'CLIENT_PROVIDER'),
);
